import { differenceInDays, isBefore, isSameMonth } from 'date-fns';
import getConfig from 'next/config';
import { useIntl } from 'react-intl';

import { EventForAdminType, EventType } from '~/components/Event';
import {
    Event_Schedule_Types_Enum,
    EventFieldsFragment,
    GetAdminEventQuery
} from '~/graphql/hasura';
import { eventIsWebUp } from '~/lib/utils/booking';
import { MICROSITE_UID } from '~/lib/utils/env';
const { publicRuntimeConfig } = getConfig();

export const renderEventCity = (event: EventFieldsFragment): string | null => {
    if (
        event.event_location &&
        event.event_location.city &&
        !event.event_location.city.match(/livestream/i)
    ) {
        return event.event_location.city;
    }
    return null;
};

export const renderEventSubtitle = (event: EventType): string => {
    const intl = useIntl();
    const [start, end] = [new Date(event.start_time), new Date(event.end_time)];
    const isOneDayEvent = differenceInDays(start, end) === 0;
    const isInSameMonth = isSameMonth(start, end);

    let onDateString;

    if (isOneDayEvent) {
        onDateString = intl.formatMessage(
            {
                id: 'event.dateformat.one_day',
                defaultMessage: '{event_start_date, date, long}'
            },
            {
                event_start_date: new Date(event.start_time)
            }
        );
    } else {
        onDateString = isInSameMonth
            ? intl.formatMessage(
                  {
                      id: 'event.dateformat.same_month',
                      defaultMessage: '{event_start_date, date, ::d} - {event_end_date, date, long}'
                  },
                  {
                      event_start_date: new Date(event.start_time),
                      event_end_date: new Date(event.end_time)
                  }
              )
            : intl.formatMessage(
                  {
                      id: 'event.dateformat.different_months',
                      defaultMessage:
                          '{event_start_date, date, ::d. LLLL} - {event_end_date, date, long}'
                  },
                  {
                      event_start_date: new Date(event.start_time),
                      event_end_date: new Date(event.end_time)
                  }
              );
    }

    const eventCity = renderEventCity(event);

    if (eventCity && !eventIsWebUp(event)) {
        return `${onDateString} in ${eventCity}`;
    }

    return onDateString;
};

export const hasEventStarted = (event: Pick<EventFieldsFragment, 'start_time'>): boolean => {
    return !isBefore(new Date(), new Date(event.start_time));
};

export const isOneDayEvent = (event: EventFieldsFragment): boolean => {
    return (
        event.event_schedules.filter((es) => es.type_id === Event_Schedule_Types_Enum.Day)
            .length === 1
    );
};

export const getEventUrl = ({
    hostname,
    event_id,
    event_locale,
    page = 'livestream'
}: {
    hostname: string;
    event_id: string;
    event_locale: string;
    page?: string;
}) => {
    const protocol = hostname.startsWith('localhost') ? 'http' : 'https';

    const localePrefix =
        publicRuntimeConfig.defaultLocale === event_locale ? '' : '/' + event_locale;

    return `${protocol}://${hostname}${localePrefix}/events/${event_id}/${page}`;
};

export const getEventSupportEmail = (eventData: EventForAdminType | undefined | null) => {
    if (eventData && eventData.support_email) {
        return eventData.support_email;
    }

    // FomF Event Site
    if (MICROSITE_UID === '8f4bf0b7-5799-4978-8190-6adc8cd6d516') {
        return 'streamsupport@fomf.org';
    }
    return 'support@forumlivemedia.org';
};

export const getEventRegistrationFromEmail = (
    eventAdminData: GetAdminEventQuery['events_by_pk']
) => {
    if (eventAdminData && eventAdminData.reg_from_email) {
        return eventAdminData.reg_from_email;
    }

    return 'event@forumlivemedia.org';
};

export const getEventRegistrationBccEmail = (
    eventAdminData: GetAdminEventQuery['events_by_pk']
) => {
    if (eventAdminData && eventAdminData.reg_bcc_email) {
        return eventAdminData.reg_bcc_email;
    }

    return '';
};
